<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Select a category') }}</h4>
    </div>
    <div class="modal-body">
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label for="category">{{ _('Category') }}</label><br />
            <v-select :options="categories" v-model="category" label="name" keys="['id', 'name']" :placeholder="_('Type to search categories')" :create-item="false" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group languageversioned">
            <label for="title">{{ _('Custom title') }}</label>
            <input id="title" type="text" class="form-control" v-model="title" :placeholder="defaultText('title', _('Enter an optional title to use in place of the category name'))" />
          </div>
        </div>
      </div>
      <div class="row" v-if="frontpageMode">
        <div class="col-md-12">
          <div class="form-group languageversioned">
            <label for="linkButtonText">{{ _('Action button text') }}</label>
            <input id="linkButtonText" type="text" class="form-control" v-model="buttontext" :placeholder="defaultText('buttontext', _('Show all'))" />
          </div>
        </div>
      </div>
      <div class="row" v-if="frontpageMode && !isDateSortedViewSelected">
        <div class="col-md-12">
          <div class="form-group">
            <label for="linkButtonPosition">{{ _('Action button position') }}</label>
            <select id="linkButtonPosition" class="form-control" v-model="buttonposition">
              <option value="">{{ _('After product list') }}</option>
              <option value="afterdescription">{{ _('After short description') }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12">
          <div class="checkbox">
            <label>
            <input type="checkbox" v-model="hidetitle" true-value="1" false-value="0" />
            {{ _('Hide title') }}
            </label>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12">
          <div class="checkbox">
            <label>
            <input type="checkbox" v-model="showdescription" true-value="1" false-value="0" />
            {{ _('Show description') }}
            </label>
          </div>
        </div>
      </div>
      <div class="row" v-if="frontpageMode">
        <div class="col-sm-12">
          <div class="checkbox">
            <label>
            <input type="checkbox" v-model="showimage" true-value="1" false-value="0" />
            {{ _('Show main image') }}
            </label>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <label for="linkedCategoryView">{{ _('Storefront view') }}</label>
          <ProductListViewSelector id="linkedCategoryView" v-model="view" :allow-default="true" />
          <p class="help-block" v-if="!frontpageMode && isDateSortedViewSelected">{{ _('Date-sorted lists show the newest products first. The action button after the list shows the rest.') }}</p>
        </div>
      </div>
      <div class="row" v-if="frontpageMode">
        <div class="col-md-12">
          <div class="form-group">
            <label for="productLimitModeLink">{{ _('Maximum number of products') }}</label>
            <select id="productLimitModeLink" class="form-control" v-model="productLimitMode">
              <option value="default">{{ _('Use front page default') }}</option>
              <option value="none">{{ _('No limit') }}</option>
              <option value="hide">{{ _('Don\'t show products') }}</option>
              <option value="limit">{{ _('Limited number') }}</option>
            </select>
            <p class="help-block" v-if="isDateSortedViewSelected">{{ _('Date-sorted lists show this many of the newest products first. The action button after the list shows the rest.') }}</p>
          </div>
        </div>
      </div>
      <div class="row" v-if="frontpageMode && productLimitMode === 'limit'">
        <div class="col-md-12">
          <div class="form-group">
            <input id="productLimitLink" type="number" min="1" step="1" data-android-inputmode="decimal" class="form-control" v-model="productLimitCount" :placeholder="_('Enter the maximum number of products')" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label for="productListColumnCount">{{ _('Product list column count') }}</label>
            <select class="form-control" id="productListColumnCount" v-model="productlistcolumncount">
              <option :value="null">{{ _('Default') }}</option>
              <option :value="2">2</option>
              <option :value="3">3</option>
              <option :value="4">4</option>
            </select>
          </div>
        </div>
      </div>
      <div class="row" v-if="isResourcePoolSelectorAvailable">
        <div class="col-md-12">
          <div class="checkbox">
            <label>
            <input type="checkbox" v-model="limitShowtimes" />
            {{ _('List showtimes only for resources in the selected resource pool') }}
            </label>
          </div>
          <div class="form-group">
            <v-select :options="resourcepools" v-model="resourcepool" label="name" keys="['id', 'name']" :placeholder="_('Type to search resource pools')" :create-item="false" />
          </div>
        </div>
      </div>
      <div class="row" v-if="isSearchPlaceholderFieldVisible">
        <div class="col-md-12">
          <div class="form-group languageversioned">
            <br />
            <label for="searchPlaceholder">{{ _('Search placeholder') }}</label>
            <input id="searchPlaceholder" type="text" class="form-control" v-model="searchplaceholder" :placeholder="defaultText('searchplaceholder', _('Enter a placeholder text to be displayed in the search field'))" />
          </div>
        </div>
      </div>
      <div style="height: 200px"></div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" v-on:click="selectCategory"><i class="fa fa-fw fa-save"></i> {{ _('OK') }}</button>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { mapGetters } from 'vuex'
import constants from '~/lib/constants'
import { isDateSortedView } from '~/lib/productListViews'
import { ProductListViewSelector } from '#components'

export default {
  components: {
    ProductListViewSelector
  },
  props: {
    selectedCategories: {
      type: Array,
      default: () => []
    },
    linkedCategory: {
      type: Object,
      default: null
    },
    frontpageMode: {
      type: Boolean,
      default: false
    }
  },
  mixins: [constants],
  data () {
    return {
      category: null,
      title: '',
      buttontext: '',
      buttonposition: '',
      hidetitle: false,
      showdescription: false,
      showimage: '1',
      productlimit: '',
      lastProductLimit: 8,
      productLimitHideSentinel: 4294967295,
      productlistcolumncount: null,
      view: '',
      searchplaceholder: '',
      resourcepool: null,
      categories: [],
      resourcepools: [],
      showtimeresourcepool_id: null,
      resourcePoolSelectorActivated: false
    }
  },
  computed: {
    ...mapGetters(['shopId', 'baseDomain', 'apiKey']),
    selectedCategoryIds () {
      return this.selectedCategories.map(l => l.id || l.category_id || l.linkedcategory_id).filter(Boolean)
    },
    selectedResourcePoolIds () {
      return this.selectedCategories.map(l => l.id)
    },
    isSearchPlaceholderFieldVisible () {
      switch (this.view) {
        case 'availabilitylist':
        case 'availabilitymap':
        case 'availabilityfulltext':
        case 'fulltextsearch':
          return true
      }
      return false
    },
    isResourcePoolSelectorAvailable () {
      return (this.view === 'showtimes')
    },
    isDateSortedViewSelected () {
      return isDateSortedView(this.view)
    },
    productLimitMode: {
      get () {
        if (this.productlimit === '' || this.productlimit === null || typeof this.productlimit === 'undefined') {
          return 'default'
        }
        const n = Number(this.productlimit)
        if (n < 0 || n === this.productLimitHideSentinel) {
          return 'hide'
        }
        if (n === 0) {
          return 'none'
        }
        if (n > 0) {
          return 'limit'
        }
        return 'default'
      },
      set (mode) {
        const current = parseInt(this.productlimit, 10)
        if (current > 0 && current !== this.productLimitHideSentinel) {
          this.lastProductLimit = current
        }
        if (mode === 'hide') {
          this.productlimit = this.productLimitHideSentinel
        } else if (mode === 'none') {
          this.productlimit = 0
        } else if (mode === 'limit') {
          this.productlimit = this.lastProductLimit || 8
        } else {
          this.productlimit = ''
        }
      }
    },
    productLimitCount: {
      get () {
        const n = parseInt(this.productlimit, 10)
        if (n > 0 && n !== this.productLimitHideSentinel) {
          return n
        }
        return this.lastProductLimit || ''
      },
      set (value) {
        const n = parseInt(value, 10)
        if (n > 0 && n !== this.productLimitHideSentinel) {
          this.lastProductLimit = n
          this.productlimit = n
        }
      }
    },
    limitShowtimes: {
      get () {
        return this.resourcePoolSelectorActivated || this.showtimeresourcepool_id
      },
      set (newValue) {
        if (!newValue) {
          this.resourcePoolSelectorActivated = false
          this.resourcepool = null
        } else {
          this.resourcePoolSelectorActivated = true
        }
      }
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    defaultText (field, fallback) {
      const texts = this.frontpageMode && this.linkedCategory && this.linkedCategory.defaulttexts
      return (texts && texts[field]) || fallback
    },
    selectCategory () {
      const payload = {
        id: (this.linkedCategory && this.linkedCategory.id ? this.linkedCategory.id : null),
        category: this.category,
        title: this.title,
        hidetitle: this.hidetitle,
        view: this.view,
        searchplaceholder: this.searchplaceholder,
        showdescription: this.showdescription,
        showtimeresourcepool_id: parseInt(this.showtimeresourcepool_id, 10),
        productlistcolumncount: [2, 3, 4].indexOf(parseInt(this.productlistcolumncount, 10)) !== -1
          ? parseInt(this.productlistcolumncount, 10)
          : null
      }
      if (this.frontpageMode) {
        payload.showimage = this.showimage
        payload.productlimit = (this.productlimit === '' || this.productlimit === null || typeof this.productlimit === 'undefined')
          ? null
          : Number(this.productlimit)
        payload.category_id = this.category ? this.category.id : null
        payload.buttontext = this.buttontext || ''
        payload.buttonposition = this.buttonposition === 'afterdescription' && !this.isDateSortedViewSelected ? 'afterdescription' : ''
      }
      this.$emit('selectCategory', payload)
    },
    loadCategories() {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/categories.json?iSortingCols=1&iSortCol_0=2&sSortDir_0=asc&apikey=' + this.apiKey)
        } catch(e) {
          resolve([])
          return
        }
        const editingId = this.linkedCategory
          ? (this.linkedCategory.linkedcategory_id || this.linkedCategory.category_id || (this.linkedCategory.category && this.linkedCategory.category.id))
          : null
        this.categories = response.data.filter(category => {
          if (editingId && parseInt(category.id, 10) === parseInt(editingId, 10)) {
            return true
          }
          return this.selectedCategoryIds.map(id => parseInt(id, 10)).indexOf(parseInt(category.id, 10)) === -1
        })
        resolve(this.categories)
      })
    },
    loadResourcePools() {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/resourcepools.json?iSortingCols=1&iSortCol_0=2&sSortDir_0=asc&apikey=' + this.apiKey)
        } catch(e) {
          resolve([])
          return
        }
        this.resourcepools = response.data.filter(resourcepool => {
          return this.selectedResourcePoolIds.indexOf(resourcepool.id) === -1
        })
        resolve(this.resourcepools)
      })
    },
    setResourcePool(resourcePoolId) {
      const resourcepool = this.resourcepools.find(c => parseInt(c.id, 10) === parseInt(resourcePoolId, 10))
      if (resourcepool) {
        this.resourcepool = resourcepool
      }
    },
    setLinkedCategory(linkedCategory) {
      const linkedId = linkedCategory.linkedcategory_id || linkedCategory.category_id || (linkedCategory.category && linkedCategory.category.id)
      const category = this.categories.find(c => parseInt(c.id, 10) === parseInt(linkedId, 10))
      if (category) {
        this.category = category
      }
      if (linkedCategory.title) {
        this.title = linkedCategory.title
      }
      if (linkedCategory.buttontext && linkedCategory.buttontext !== '0') {
        this.buttontext = linkedCategory.buttontext
      }
      this.buttonposition = linkedCategory.buttonposition === 'afterdescription' ? 'afterdescription' : ''
      if (linkedCategory.showdescription !== undefined && linkedCategory.showdescription !== null) {
        this.showdescription = linkedCategory.showdescription
      }
      if (linkedCategory.showimage !== undefined && linkedCategory.showimage !== null) {
        this.showimage = linkedCategory.showimage
      }
      this.productlimit = ''
      if (linkedCategory.productlimit !== undefined && linkedCategory.productlimit !== null && linkedCategory.productlimit !== '') {
        this.productlimit = linkedCategory.productlimit
        const n = parseInt(linkedCategory.productlimit, 10)
        if (n > 0 && n !== this.productLimitHideSentinel) {
          this.lastProductLimit = n
        }
      }
      const productListColumns = parseInt(linkedCategory.productlistcolumncount, 10)
      this.productlistcolumncount = [2, 3, 4].indexOf(productListColumns) !== -1 ? productListColumns : null
      if (linkedCategory.hidetitle) {
        this.hidetitle = linkedCategory.hidetitle
      }
      this.view = (linkedCategory.view && linkedCategory.view !== '0') ? linkedCategory.view : ''
      if (linkedCategory.searchplaceholder) {
        this.searchplaceholder = linkedCategory.searchplaceholder
      }
      if (linkedCategory.showtimeresourcepool_id) {
        this.showtimeresourcepool_id = linkedCategory.showtimeresourcepool_id
      }
    }
  },
  watch: {
    resourcepool: {
      deep: true,
      handler (newValue) {
        if(newValue && newValue.id) {
          this.showtimeresourcepool_id = newValue.id
        } else {
        this.showtimeresourcepool_id = null
        }
      }
    } 
  },
  async mounted () {
    this.view = ''
    if (this.frontpageMode) {
      this.showdescription = '1'
      this.showimage = '1'
    }
    await this.loadCategories()
    if (this.linkedCategory) {
      this.setLinkedCategory(this.linkedCategory)
    }
    await this.loadResourcePools()
    if (this.showtimeresourcepool_id) {
      this.setResourcePool(this.showtimeresourcepool_id)
    }
  }
}
</script>
